import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gauge, History, RotateCcw } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fmtDate } from "../../lib/status";

const SOURCE_LABEL = {
  pickup_handover: "Pickup handover", return_inspection: "Return handover",
  maintenance: "Maintenance service", manual: "Manual correction", backfill: "Backfilled",
};

/**
 * Phase 2G: current mileage + auditable history, self-fetching by
 * vehicleId (same convention as PickupHandoverPanel). Every reading shows
 * its source/booking/actor/timestamp, never a bare number with no trace.
 */
export function VehicleMileageHistoryCard({ vehicleId, currentMileageKm, editAllowed }) {
  const [items, setItems] = useState(null);
  const [showCorrect, setShowCorrect] = useState(false);
  const [mileage, setMileage] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/vehicles/${vehicleId}/mileage-history`);
      setItems(data.items || []);
    } catch (_) {
      setItems([]);
    }
  }, [vehicleId]);
  useEffect(() => {
    setShowCorrect(false);
    setMileage("");
    setReason("");
    reload();
  }, [reload]);

  const toggleCorrection = () => {
    if (showCorrect) {
      setMileage("");
      setReason("");
    }
    setShowCorrect((value) => !value);
  };

  const latest = items?.[0];

  const submitCorrection = async (e) => {
    e.preventDefault();
    if (mileage === "" || !reason.trim()) {
      toast.error("Mileage and a reason are both required.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/vehicles/${vehicleId}/mileage/correct`,
        { mileage_km: Number(mileage), reason: reason.trim() });
      toast.success("Mileage corrected");
      setShowCorrect(false); setMileage(""); setReason("");
      await reload();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-3" data-testid="vd-mileage-history">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-lg font-semibold inline-flex items-center gap-2">
            <Gauge className="w-4 h-4 text-[#1B4332]" /> Mileage
          </h2>
          <p className="text-sm text-slate-600 mt-0.5">
            {currentMileageKm != null ? (
              <>Current mileage <span className="font-semibold">{Number(currentMileageKm).toLocaleString()} km</span></>
            ) : "No mileage recorded yet."}
            {latest && (
              <span className="text-slate-500"> · Last updated from {SOURCE_LABEL[latest.source] || latest.source}
                {latest.booking_id ? "" : ""} · {fmtDate(latest.created_at)}</span>
            )}
          </p>
        </div>
        {editAllowed && (
          <button onClick={toggleCorrection} data-testid="vd-mileage-correct-toggle"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">
            <RotateCcw className="w-3.5 h-3.5" /> Correct mileage
          </button>
        )}
      </div>

      {showCorrect && (
        <form onSubmit={submitCorrection} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 flex flex-wrap items-end gap-2"
              data-testid="vd-mileage-correct-form">
          <label className="text-sm">
            <span className="text-slate-700 font-medium block">Correct mileage (km)</span>
            <input type="number" min="0" value={mileage} onChange={(e) => setMileage(e.target.value)}
                   data-testid="vd-mileage-correct-value"
                   className="mt-1 px-3 py-2 rounded-lg border border-slate-300 w-40" />
          </label>
          <label className="text-sm flex-1 min-w-[200px]">
            <span className="text-slate-700 font-medium block">Reason</span>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                   data-testid="vd-mileage-correct-reason"
                   className="mt-1 px-3 py-2 rounded-lg border border-slate-300 w-full" />
          </label>
          <button type="submit" disabled={saving} data-testid="vd-mileage-correct-submit"
                  className="px-4 py-2 rounded-lg bg-[#1B4332] text-white text-sm disabled:opacity-60">
            {saving ? "Saving…" : "Save"}
          </button>
        </form>
      )}

      <div>
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
          <History className="w-3.5 h-3.5" /> History
        </div>
        {items === null ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500">No readings recorded yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100 text-sm">
            {items.map((r) => (
              <li key={r.id} className="py-2 flex items-center justify-between gap-2" data-testid={`vd-mileage-reading-${r.id}`}>
                <div>
                  <span className="font-medium">{r.mileage_km.toLocaleString()} km</span>
                  <span className="text-slate-500 ml-2">
                    {SOURCE_LABEL[r.source] || r.source}
                    {r.is_correction && <span className="ml-1 text-amber-700">(correction{r.correction_reason ? `: ${r.correction_reason}` : ""})</span>}
                  </span>
                </div>
                <span className="text-slate-400 text-xs">{fmtDate(r.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
